import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  TouchableOpacity,
  Image,
  ScrollView,
  Modal,
  Pressable,
  ActivityIndicator,
  Alert,
  Share,
  Platform,
  Animated,
} from 'react-native';
import Clipboard from '@react-native-clipboard/clipboard';
import QRCode from 'react-native-qrcode-svg';
import type { V2Balance } from '../../../types/v2';
import SimpleHeader from '../../../components/SimpleHeader';
import TabBar from '../../../components/TabBar';
import TokenIcon from '../../../components/UIComponents/TokenIcon';
import { CARD_SURFACE, COLORS } from '../../../theme/themeTokens';
import { v2GetMyBalances } from '../../../services/v2/api';
import { formatTokenAmount } from '../../../utils/amountFormat';
import { useSwipeDownDismiss } from '../../../hooks/useSwipeDownDismiss';
import { WEB_SHEET, WEB_SHEET_BACKDROP } from '../../../../web/sheet';
import { compareByNetwork } from '../../../utils/networkOrder';

// Top inset used by the account-picker sheet when rendered inside
// OperationsScreen. Aligns the sheet top just BELOW the screen's
// "Source account" label so the header stays visible above the sheet.
// Breakdown:
//   status bar gutter (20 iOS / 28 Android)
//   + Operations header (44) + 12-dp gap + 44-dp tab strip   = +100
//   + scrollContent.paddingTop (16) + sectionLabel.marginTop (16) = +32
//   + sectionLabel height (~17) + sectionLabel.marginBottom (12) = +29
const OPERATIONS_TOP_INSET = (Platform.OS === 'android' ? 28 : 20) + 161;

// Format an amount the same way AccountCard does on the Current Account
// screen — NBSP as thousands separator, comma as decimal separator, decimals
// taken from the token catalog (v1_2_tokens.decimals) so the row matches the
// BalanceDetail tile exactly.
// formatBalanceLikeCard — delegate к formatTokenAmount для string-truncation
// consistency с Maximum-кнопкой. Раньше toFixed округлял вверх и давал
// рассинхрон между балансом и кликом Maximum (см. amountFormat.ts).
// Потолок знаков на экране живёт в formatTokenAmount: token_decimals — это
// точность контракта (18 у USDT), а не то, сколько цифр читает человек.
const formatBalanceLikeCard = (amount: number | string, decimals = 2): string =>
  formatTokenAmount(amount, decimals);

interface ConfirmDepositScreenProps {
  balance: V2Balance;
  onBack: () => void;
  onNavigate: (screen: string) => void;
  /**
   * Legacy callback fired when the user confirmed the network. Kept in the
   * prop signature for backwards-compatibility with App.tsx wiring, but no
   * longer invoked — this screen now shows the address inline so there's
   * no separate "confirm → next" step.
   */
  onConfirm?: (selectedBalance: V2Balance) => void;
  /** When true, parent (OperationsScreen) supplies header + tabbar, so this
   *  screen renders body-only. */
  embedded?: boolean;
}

/**
 * One row in the current-account picker. Combinations are (token × network),
 * so users see e.g. "USDT — TRC20", "USDT — ERC20", "BTC — BTC" instead of a
 * collapsed token total. The underlying balance row stays attached so picking
 * an option drives the address / QR section directly without a second lookup.
 */
interface AccountPickerOption {
  /** Stable React key — natural key (token+network+wallet). */
  key: string;
  symbol: string;
  networkSymbol: string;
  networkName: string;
  tokenName: string;
  iconUrl: string | null;
  amount: number;
  balance: V2Balance;
}

const ConfirmDepositScreen: React.FC<ConfirmDepositScreenProps> = ({
  balance,
  onBack,
  onNavigate,
  embedded = false,
}) => {
  // Flat picker — one row per (token × network) combination across all
  // current-account real balances. No collapsing by symbol so the user sees
  // e.g. "USDT — TRC20" and "USDT — ERC20" as separate selectable rows.
  const [pickerOptions, setPickerOptions] = useState<AccountPickerOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<AccountPickerOption | null>(null);
  const [pickerModalVisible, setPickerModalVisible] = useState(false);
  // Swipe-to-dismiss for the Current-accounts picker bottom-sheet.
  // Shared hook → identical behaviour everywhere the same gesture
  // ships (NumericKeypad and the other deposit-flow pickers as we
  // roll them out).
  const pickerSwipe = useSwipeDownDismiss(
    pickerModalVisible,
    () => setPickerModalVisible(false),
  );
  const [loadingPicker, setLoadingPicker] = useState(true);

  // Full balances list — cached so we don't re-fetch on selection change.
  const [allBalances, setAllBalances] = useState<V2Balance[]>([]);

  // Picking a row in the modal immediately resolves to a specific balance, so
  // a separate network-picker isn't needed for this flow.
  const selectedNetwork: V2Balance | null = selectedOption?.balance ?? null;

  // Effective token symbol (from picker, falling back to the prop).
  const currency = selectedOption?.symbol || balance.token_symbol || 'USDT';


  // Display values from selected network row.
  const activeNetwork = selectedNetwork || balance;

  // One-shot fetch of all user balances. Used to populate the flat picker AND
  // to compute the virtual-aggregate amount for the Source-account display.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingPicker(true);
        const res = await v2GetMyBalances();
        if (cancelled) return;

        const all = res.balances ?? [];
        setAllBalances(all);

        const currentReal = all.filter(
          (b) =>
            (b.account_type_code ?? 'current') === 'current' &&
            b.provider_name !== 'virtual',
        );

        const opts: AccountPickerOption[] = currentReal.map((b) => ({
          key: `${b.token_symbol}|${b.network_symbol || 'no-net'}|${b.wallet_address || b.wallet_id || b.id}`,
          symbol: b.token_symbol,
          networkSymbol: b.network_symbol || '',
          networkName: b.network_name || '',
          tokenName: b.token_name || b.token_symbol,
          iconUrl: b.icon_url ?? null,
          // Use available_amount (amount − locked_amount) so the picker
          // value matches what the rest of the screen shows; raw `amount`
          // would double-count pending withdraw/top-up locks.
          amount: parseFloat(b.available_amount ?? b.amount ?? '0') || 0,
          balance: b,
        }));

        // Network-rank first (TRON → Ethereum → others); USDT before other
        // tokens within the same network rank; alphabetical for the rest.
        // Same ordering as WithdrawScreen / DepositCardScreen — shared via
        // utils/networkOrder.
        const tokenRank = (s: string) => (s.toUpperCase() === 'USDT' ? 0 : 1);
        opts.sort((a, b) => {
          const byNetwork = compareByNetwork(a, b);
          if (byNetwork !== 0) return byNetwork;
          const ta = tokenRank(a.symbol);
          const tb = tokenRank(b.symbol);
          if (ta !== tb) return ta - tb;
          return a.symbol.localeCompare(b.symbol);
        });

        setPickerOptions(opts);

        // Land on the network of the arriving token that actually holds
        // something. Opening Inflow always on the top row — USDT-TRC20 —
        // meant a client whose USDT sits on Ethereum was shown a Tron
        // address to receive at, and had to notice and switch.
        //
        // Among that token's networks: the largest available amount wins;
        // an even draw, which is what two empty networks are, falls to
        // compareByNetwork and so to Tron. One network needs no choosing.
        // A token with no rows at all keeps the old top-row default.
        const ofToken = opts.filter((o) => o.symbol === balance.token_symbol);
        const preferred = [...ofToken].sort((a, b) => {
          if (a.amount !== b.amount) return b.amount - a.amount;
          return compareByNetwork(a, b);
        })[0];
        setSelectedOption(preferred ?? opts[0] ?? null);
      } catch (err) {
        console.warn('[ConfirmDeposit] Failed to load balances:', err);
      } finally {
        if (!cancelled) {
          setLoadingPicker(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [balance.id, balance.token_symbol]);

  const handleSelectOption = (opt: AccountPickerOption) => {
    setSelectedOption(opt);
    setPickerModalVisible(false);
  };

  const handleCopyAddress = () => {
    const addr = activeNetwork.wallet_address;
    if (!addr) { return; }
    Clipboard.setString(addr);
    Alert.alert('Copied', 'Address copied to clipboard');
  };

  // Open the native share sheet with the deposit address. Mirrors the
  // pattern from CardScreen TransactionDetail (`Share.share({ message })`).
  const handleShareAddress = async () => {
    const addr = activeNetwork.wallet_address;
    if (!addr) { return; }
    try {
      await Share.share({
        message: addr,
        title: 'Deposit address',
      });
    } catch (err) {
      console.warn('[ConfirmDeposit] Share error:', err);
    }
  };

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />

      {!embedded && (
        <SimpleHeader
          title="Deposit"
          onBack={onBack}
          backgroundVariant="primaryNavy"
        />
      )}

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        bounces={false}
      >
        {/* Current-account picker — one row per (token × network) combination
            across all of the user's current-account real balances. Selecting a
            row directly resolves token + network + deposit address below. */}
        <Text style={styles.sectionLabel}>To current account</Text>
        {loadingPicker ? (
          <View style={[styles.card, styles.cardCenter]}>
            <ActivityIndicator size="small" color={COLORS.brandOrange} />
          </View>
        ) : (
          <TouchableOpacity
            style={styles.card}
            onPress={() => setPickerModalVisible(true)}
            activeOpacity={0.7}
          >
            <TokenIcon
              symbol={currency}
              iconUrl={selectedOption?.iconUrl}
              style={styles.currencyIcon}/>
            <View style={styles.networkTextContainer}>
              <Text style={styles.networkName}>
                {selectedOption
                  ? selectedOption.networkSymbol
                    ? `${selectedOption.symbol} - ${selectedOption.networkSymbol}`
                    : selectedOption.symbol
                  : 'No current-account balances'}
              </Text>
              {selectedOption?.networkName ? (
                <Text style={styles.networkSubtitle}>
                  {selectedOption.networkName}
                </Text>
              ) : null}
            </View>
            {selectedOption ? (
              <Text style={styles.cardAmount}>
                {formatBalanceLikeCard(selectedOption.amount, selectedOption.balance.token_decimals)} {selectedOption.symbol}
              </Text>
            ) : null}
            <Image
              source={require('../../../assets/arrow_left_20.png')}
              style={styles.chevronIcon}
              resizeMode="contain"
            />
          </TouchableOpacity>
        )}

        {/* Deposit address frame — same white card chrome as the picker above.
            Contains the QR code on top and the address row (text + copy icon)
            below. Driven by selectedNetwork.wallet_address, so picking a
            different option in the modal instantly swaps both. */}
        <View style={styles.addressFrame}>
          {activeNetwork.wallet_address ? (
            <View style={styles.qrContainer}>
              <View style={styles.qrWrapper}>
                <QRCode
                  value={activeNetwork.wallet_address}
                  size={160}
                  color={COLORS.primaryNavy}
                  backgroundColor={COLORS.white}
                />
              </View>
            </View>
          ) : null}

          <View style={styles.addressRow}>
            <Text style={styles.addressText} selectable>
              {activeNetwork.wallet_address || 'No address available'}
            </Text>
          </View>
        </View>

        <View style={styles.infoFrame}>
          <Image
            source={require('../../../assets/warning.png')}
            style={styles.infoIcon}
            resizeMode="contain"
          />
          <Text style={styles.infoText}>
            The network above must match the{'\n'}
            sending network in the other application.{'\n'}
            Otherwise, funds may be lost.
          </Text>
        </View>

        <View style={styles.actionRow}>
          <TouchableOpacity
            style={[styles.copyButton, styles.shareButton]}
            onPress={handleShareAddress}
            activeOpacity={0.8}
            disabled={!activeNetwork.wallet_address}
          >
            <Text style={[styles.copyButtonText, styles.shareButtonText]}>Share</Text>
            <Image
              source={require('../../../assets/share.png')}
              style={styles.shareButtonIcon}
              resizeMode="contain"
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.copyButton, styles.actionRowButton]}
            onPress={handleCopyAddress}
            activeOpacity={0.8}
            disabled={!activeNetwork.wallet_address}
          >
            <Text style={styles.copyButtonText}>Copy address</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Current-account picker bottom-sheet — one row per (token × network). */}
      <Modal
        visible={pickerModalVisible}
        transparent
        animationType="slide"
        statusBarTranslucent
        onRequestClose={() => setPickerModalVisible(false)}
      >
        <Pressable
          style={[styles.modalOverlay, WEB_SHEET_BACKDROP]}
          onPress={() => setPickerModalVisible(false)}
        >
          <Animated.View
            style={[
              styles.modalContent,
              embedded && { marginTop: OPERATIONS_TOP_INSET },
              WEB_SHEET,
              { transform: [{ translateY: pickerSwipe.translateY }] },
            ]}
            {...pickerSwipe.panHandlers}
          >
            <Pressable onPress={(e) => e.stopPropagation()}>
              <View style={styles.modalHandle} />
              <View style={styles.modalTitleRow}>
                <Text style={styles.modalTitle}>Current accounts</Text>
                <Text style={styles.modalTitleAside}>Available balance</Text>
              </View>
            {pickerOptions.length === 0 ? (
              <Text style={[styles.modalOptionSubtitle, { paddingHorizontal: 12 }]}>
                No current-account balances available.
              </Text>
            ) : null}
            {pickerOptions.map((opt) => {
              const isSelected = selectedOption?.key === opt.key;
              const title = opt.networkSymbol
                ? `${opt.symbol} - ${opt.networkSymbol}`
                : opt.symbol;
              return (
                <TouchableOpacity
                  key={opt.key}
                  style={[styles.modalOption, isSelected && styles.modalOptionSelected]}
                  onPress={() => handleSelectOption(opt)}
                  activeOpacity={0.7}
                >
                  <TokenIcon
                    symbol={opt.symbol}
                    iconUrl={opt.iconUrl}
                    style={styles.modalOptionIcon}/>
                  <View style={styles.modalOptionText}>
                    <Text style={styles.modalOptionName}>{title}</Text>
                    {opt.networkName ? (
                      <Text style={styles.modalOptionSubtitle}>{opt.networkName}</Text>
                    ) : null}
                  </View>
                  <Text style={styles.modalOptionAmount}>
                    {formatBalanceLikeCard(opt.amount, opt.balance.token_decimals)} {opt.symbol}
                  </Text>
                </TouchableOpacity>
              );
            })}
            </Pressable>
          </Animated.View>
        </Pressable>
      </Modal>

      {!embedded && <TabBar activeTab="dashboard" onNavigate={onNavigate} />}
    </View>
  );
};
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },
  scrollView: {
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: 'hidden',
  },
  scrollContent: {
    // 16 dp left/right matches WithdrawScreen.contentContainer and
    // DepositCardScreen.scrollContent — three Operations tabs share the
    // same screen-edge padding now.
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 32,
  },
  /* Section label */
  sectionLabel: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    // lineHeight mirrors WithdrawScreen.sectionLabel — keeps the "To
    // current account" / "From current account" labels aligned across
    // Operations tabs.
    lineHeight: 17,
    color: COLORS.blueSteel,
    marginBottom: 8,
    marginTop: 16,
  },
  /* "To current account" tile — same chrome as Withdraw.networkSelector
     and DepositCardScreen.pickerTile so the three Operations tabs share
     one tile shape (66 high, 8-dp corners, 1-dp muted border, no shadow). */
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 66,
    paddingHorizontal: 14,
    paddingVertical: 14,
    ...CARD_SURFACE,
    // 16 dp gap to the next element (addressFrame) — uniform with the
    // rest of the stack. Was 8, which combined with addressFrame.marginTop
    // (16) to a 24-dp gap.
    marginBottom: 16,
  },
  cardCenter: {
    justifyContent: 'center',
    minHeight: 64,
  },
  currencyIcon: {
    // 28 dp matches DepositCardScreen.tokenIcon / WithdrawScreen
    // .networkSelectorIcon / OpenDepositScreen.networkSelectorIcon —
    // unified icon size in the "To current account" picker tile across
    // Operations tabs.
    width: 28,
    height: 28,
    marginRight: 12,
  },
  cardAccountName: {
    flex: 1,
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    color: COLORS.textPrimary,
  },
  cardAmount: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    // Matches WithdrawScreen.pickerAmount / DepositCardScreen.pickerAmount —
    // same lineHeight + right-aligned + left margin so the amount column
    // sits identically across the three Operations tiles.
    lineHeight: 22,
    color: COLORS.textPrimary,
    marginLeft: 8,
    textAlign: 'right',
  },
  /* Account picker card — text column. */
  networkTextContainer: {
    flex: 1,
  },
  networkName: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    // lineHeight matches WithdrawScreen.networkName + DepositCardScreen.pickerTitle.
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  networkSubtitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    fontStyle: 'normal',
    // Same as WithdrawScreen.networkSubtitle + DepositCardScreen.pickerSubtitle.
    lineHeight: 16,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  chevronIcon: {
    width: 20,
    height: 20,
    marginLeft: 8,
    transform: [{ rotate: '-90deg' }],
  },
  /* Section label center variant — used for "Deposit address" heading. */
  sectionLabelCenter: {
    textAlign: 'center',
    marginTop: 24,
  },
  /* White frame holding the QR + address row. Matches the tile style of
     `card` above (and Withdraw/Top-up plashkas): 8-dp corners, 1-dp muted
     border, no shadow — only the outline frames the QR area. */
  addressFrame: {
    ...CARD_SURFACE,
    paddingHorizontal: 16,
    paddingVertical: 16,
    // Top gap owned by the picker tile (card.marginBottom: 16); this
    // frame owns the 16-dp gap to the infoFrame below.
    marginTop: 0,
    marginBottom: 16,
  },
  /* Row with address text on the left and copy icon on the right. */
  addressRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  /* Deposit address — selectable address text inside the frame.
     Centered so the address (and the "No address available"
     fallback) sits in the middle of the row, balanced with the
     copy-button on the right. */
  addressText: {
    flex: 1,
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.textPrimary,
    textAlign: 'center',
  },
  /* QR code wrapper. */
  qrContainer: {
    alignItems: 'center',
    marginBottom: 12,
  },
  qrWrapper: {
    padding: 12,
    backgroundColor: COLORS.white,
    borderRadius: 8,
  },
  /* Copy address button — same brand-orange CTA as before. */
  copyButton: {
    backgroundColor: COLORS.primaryNavy,
    borderRadius: 8,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  copyButtonText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.white,
  },
  /* Side-by-side row hosting Share + Copy address. Each button takes half
     the available width via flex: 1 on actionRowButton. */
  actionRow: {
    flexDirection: 'row',
    gap: 12,
  },
  actionRowButton: {
    flex: 1,
    marginBottom: 20,
  },
  /* Share button — outlined variant of copyButton so the primary CTA stays
     visually dominant. */
  shareButton: {
    flex: 1,
    flexDirection: 'row',
    marginBottom: 20,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: COLORS.primaryNavy,
  },
  shareButtonText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.primaryNavy,
  },
  shareButtonIcon: {
    width: 18,
    height: 18,
    marginLeft: 8,
    tintColor: COLORS.primaryNavy,
  },
  /* Wraps the hint above the Copy button so the warning reads as a callout
     rather than plain body text. Soft-amber callout: cream fill + amber border. */
  infoFrame: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF7E3',
    borderWidth: 1,
    borderColor: '#D48600',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 16,
  },
  infoIcon: {
    width: 20,
    height: 20,
    marginRight: 12,
    tintColor: '#D48600',
  },
  infoText: {
    flex: 1,
    fontFamily: 'Inter',
    fontSize: 12,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 14,
    color: '#D48600',
    textAlign: 'left',
  },
  /* Network selection modal. Overlay stays transparent — matches the
     Withdraw / Top-up tabs where the picker slides up without dimming
     the rest of the screen. Tap on the transparent area still closes
     via the Pressable's onPress. */
  modalOverlay: {
    flex: 1,
    backgroundColor: 'transparent',
    justifyContent: 'flex-end',
  },
  modalContent: {
    // flex:1 stretches the sheet to the full overlay height so the top
    // of the picker aligns with the top of the screen (matches the
    // Withdraw / Top-up screens — single behaviour across Operations).
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 32,
  },
  modalHandle: {
    width: 40,
    height: 4,
    backgroundColor: COLORS.softGrey,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 16,
  },
  /* Title row: left-aligned heading + right-aligned aside used over the
     two visual columns of each option (label/network on the left, amount
     on the right). */
  modalTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  modalTitle: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.textPrimary,
  },
  modalTitleAside: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '500',
    color: COLORS.blueSteel,
  },
  modalOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginBottom: 4,
    backgroundColor: COLORS.white,
  },
  modalOptionSelected: {
    backgroundColor: COLORS.white,
    borderColor: COLORS.brandOrange,
    borderWidth: 1,
    borderRadius: 8,
  },
  modalOptionIcon: {
    // 28 dp matches the picker tile + the equivalent icons on Top-up /
    // Withdraw / Open deposit — single icon size across Operations tabs.
    width: 28,
    height: 28,
    marginRight: 12,
  },
  modalOptionText: {
    flex: 1,
  },
  /* Modal row typography mirrors the selected Account card (networkName /
     networkSubtitle / cardAmount), so the picked option doesn't look
     visually different from the rest of the list. */
  modalOptionName: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.textPrimary,
  },
  modalOptionSubtitle: {
    fontFamily: 'Inter',
    fontSize: 12,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 14,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  modalOptionAmount: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.textPrimary,
    marginLeft: 8,
    textAlign: 'right',
  },
  checkmark: {
    fontSize: 20,
    color: COLORS.mint,
    fontWeight: '600',
  },
});

export default ConfirmDepositScreen;
